import { createApi } from "@reduxjs/toolkit/query/react";

import {
  type IAiChat,
  type IAiChatMessage,
  type ICreateAiChat, type IGetAiChatMessagesResponse,
  type ISendAiChatMessage,
} from "@/providers/store/api/ai-chat/ai-chat.interface";
import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

const aiChatApi = createApi({
  reducerPath: "aiChatApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.AiChats, ApiTags.AiChatMessages],
  endpoints: (builder) => ({
    getAiChats: builder.query<IAiChat[], { districtId: string; search?: string }>({
      query: ({ districtId, search }) => ({
        url: `/districts/${districtId}/chats`,
        method: "GET",
        params: {
          search,
        },
      }),
      providesTags: safeInvalidateTags((result, error, { districtId }) => [{ type: ApiTags.AiChats, id: districtId }]),
      transformResponse: (result: IApiResponse<IAiChat[]>) => result.data ?? [],
    }),
    createAiChat: builder.mutation<IAiChat, ICreateAiChat>({
      query: ({
        message, districtId,
      }) => ({
        url: `/districts/${districtId}/chats`,
        method: "POST",
        body: {
          message,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.AiChats]),
      transformResponse: (result: IApiResponse<IAiChat>) => result.data,
    }),
    getAiChatMessages: builder.query<IGetAiChatMessagesResponse, { districtId: string; chatId: string; page?: number; limit?: number }>({
      query: ({ districtId, chatId, page, limit }) => ({
        url: `/districts/${districtId}/chats/${chatId}/messages`,
        method: "GET",
        params: {
          page,
          limit,
        },
      }),
      providesTags: safeInvalidateTags([{ type: ApiTags.AiChatMessages }]),
      transformResponse: (result: IApiResponse<IGetAiChatMessagesResponse>) => result.data,
    }),
    sendAiChatMessage: builder.mutation<[IAiChatMessage, IAiChatMessage], ISendAiChatMessage>({
      query: ({
        message, districtId, chatId, action,
      }) => ({
        url: `/districts/${districtId}/chats/${chatId}/messages`,
        method: "POST",
        body: {
          content: message.content,
          ...(action ? { action } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { chatId, districtId }) => [
        { type: ApiTags.AiChatMessages, id: chatId },
        { type: ApiTags.AiChats, id: districtId },
      ],
      transformResponse: (result: IApiResponse<[IAiChatMessage, IAiChatMessage]>) => result.data,
    }),
    deleteAiChat: builder.mutation<void, { districtId: string; chatId: string }>({
      query: ({ districtId, chatId }) => ({
        url: `/districts/${districtId}/chats/${chatId}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.AiChats, ApiTags.AiChatMessages]),
    }),
    submitAiChatMessageFeedback: builder.mutation<{ id: string }, {
      districtId: string;
      chatId: string;
      messageId: string;
      reason?: string;
      source?: "manual" | "escalation_prompt";
    }>({
      query: ({ districtId, chatId, messageId, reason, source }) => ({
        url: `/districts/${districtId}/chats/${chatId}/messages/${messageId}/feedback`,
        method: "POST",
        body: { reason: reason ?? "", ...(source ? { source } : {}) },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.AiChatMessages]),
      transformResponse: (result: IApiResponse<{ id: string }>) => result.data,
    }),
    dismissAiChatSupportPrompt: builder.mutation<void, {
      districtId: string;
      chatId: string;
      messageId: string;
    }>({
      query: ({ districtId, chatId, messageId }) => ({
        url: `/districts/${districtId}/chats/${chatId}/messages/${messageId}/support-prompt/dismiss`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.AiChatMessages]),
      transformResponse: () => undefined,
    }),
  }),
});

export const {
  useGetAiChatsQuery,
  useCreateAiChatMutation,
  useGetAiChatMessagesQuery,
  useSendAiChatMessageMutation,
  useDeleteAiChatMutation,
  useSubmitAiChatMessageFeedbackMutation,
  useDismissAiChatSupportPromptMutation,
} = aiChatApi;
export default aiChatApi;

